/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  DeploymentExternalMCP,
  DeploymentExternalMCP$inboundSchema,
} from "./deploymentexternalmcp.js";
import {
  DeploymentFunctions,
  DeploymentFunctions$inboundSchema,
} from "./deploymentfunctions.js";
import {
  DeploymentPackage,
  DeploymentPackage$inboundSchema,
} from "./deploymentpackage.js";
import {
  OpenAPIv3DeploymentAsset,
  OpenAPIv3DeploymentAsset$inboundSchema,
} from "./openapiv3deploymentasset.js";

export type GetDeploymentResult = {
  /**
   * The ID of the deployment that this deployment was cloned from.
   */
  clonedFrom?: string | undefined;
  /**
   * The creation date of the deployment.
   */
  createdAt: Date;
  /**
   * The external ID to refer to the deployment. This can be a git commit hash for example.
   */
  externalId?: string | undefined;
  /**
   * The number of tools in the deployment generated from external MCP servers.
   */
  externalMcpToolCount: number;
  /**
   * The external MCP servers that were deployed.
   */
  externalMcps?: Array<DeploymentExternalMCP> | undefined;
  /**
   * The upstream URL a deployment can refer to. This can be a github url to a commit hash or pull request.
   */
  externalUrl?: string | undefined;
  /**
   * The IDs, as returned from the assets upload service, to uploaded OpenAPI 3.x documents whose operations will become tool definitions.
   */
  functionsAssets?: Array<DeploymentFunctions> | undefined;
  /**
   * The number of tools in the deployment generated from Functions.
   */
  functionsToolCount: number;
  /**
   * The github pull request that resulted in the deployment.
   */
  githubPr?: string | undefined;
  /**
   * The github repository in the form of "owner/repo".
   */
  githubRepo?: string | undefined;
  /**
   * The commit hash that triggered the deployment.
   */
  githubSha?: string | undefined;
  /**
   * The ID to of the deployment.
   */
  id: string;
  /**
   * A unique identifier that will mitigate against duplicate deployments.
   */
  idempotencyKey?: string | undefined;
  /**
   * The IDs, as returned from the assets upload service, to uploaded OpenAPI 3.x documents whose operations will become tool definitions.
   */
  openapiv3Assets: Array<OpenAPIv3DeploymentAsset>;
  /**
   * The number of tools in the deployment generated from OpenAPI documents.
   */
  openapiv3ToolCount: number;
  /**
   * The ID of the organization that the deployment belongs to.
   */
  organizationId: string;
  /**
   * The packages that were deployed.
   */
  packages: Array<DeploymentPackage>;
  /**
   * The ID of the project that the deployment belongs to.
   */
  projectId: string;
  /**
   * The status of the deployment.
   */
  status: string;
  /**
   * The ID of the user that created the deployment.
   */
  userId: string;
};

/** @internal */
export const GetDeploymentResult$inboundSchema: z.ZodMiniType<
  GetDeploymentResult,
  unknown
> = z.pipe(
  z.object({
    cloned_from: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    external_id: z.optional(z.string()),
    external_mcp_tool_count: z.int(),
    external_mcps: z.optional(z.array(DeploymentExternalMCP$inboundSchema)),
    external_url: z.optional(z.string()),
    functions_assets: z.optional(z.array(DeploymentFunctions$inboundSchema)),
    functions_tool_count: z.int(),
    github_pr: z.optional(z.string()),
    github_repo: z.optional(z.string()),
    github_sha: z.optional(z.string()),
    id: z.string(),
    idempotency_key: z.optional(z.string()),
    openapiv3_assets: z.array(OpenAPIv3DeploymentAsset$inboundSchema),
    openapiv3_tool_count: z.int(),
    organization_id: z.string(),
    packages: z.array(DeploymentPackage$inboundSchema),
    project_id: z.string(),
    status: z.string(),
    user_id: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "cloned_from": "clonedFrom",
      "created_at": "createdAt",
      "external_id": "externalId",
      "external_mcp_tool_count": "externalMcpToolCount",
      "external_mcps": "externalMcps",
      "external_url": "externalUrl",
      "functions_assets": "functionsAssets",
      "functions_tool_count": "functionsToolCount",
      "github_pr": "githubPr",
      "github_repo": "githubRepo",
      "github_sha": "githubSha",
      "idempotency_key": "idempotencyKey",
      "openapiv3_assets": "openapiv3Assets",
      "openapiv3_tool_count": "openapiv3ToolCount",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "user_id": "userId",
    });
  }),
);

export function getDeploymentResultFromJSON(
  jsonString: string,
): SafeParseResult<GetDeploymentResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => GetDeploymentResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'GetDeploymentResult' from JSON`,
  );
}
